import { useEffect, useState } from 'preact/hooks';
import { getCoachMe, saveCoachProfile, type CoachProfileApi } from '../lib/api';
import { PortalFrame } from '../components/PortalFrame';
import { CoachQueue } from './CoachQueue';
import { CoachStudent } from './CoachStudent';
import { CoachStudents } from './CoachStudents';
import { CoachReport } from './CoachReport';
import './CoachShell.css';

// CoachShell — the coach surface's shell (rung 1).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26`
//
// ── WHY THIS IS A SHELL OF ITS OWN, AND NOT A TAB ──
//
// A coach is an outside professional who reviews tapes for many households
// and belongs to NONE of them. They have a session but no tenant and no
// membership, so every tenant-scoped screen in this app is wrong for them —
// not merely unhelpful, but actively broken: each one would fire calls that
// 401/403 while still showing them the NAMES of somebody else's screens.
// That is precisely the injury B18b fixed for clients (App.tsx's ClientPortal
// branch), and this is the same shape one door over.
//
// ── WHY IT SITS WHERE IT SITS IN App.tsx ──
//
// AFTER the signed-out gate (a coach must sign in — this door is not
// anonymous, unlike the token portal) and BEFORE the tenant gate. Before,
// because `TenantResolver` would otherwise push a coach to CREATE A
// HOUSEHOLD, which is exactly the thing a coach must never have. A coach who
// ends up with a membership stops being outside the family, and every
// tenant-scoped people list would quietly start including them.
//
// ── THE URL ──
//
// Reached at the hash route `#/pro`, matching the reviewer portal's own
// `#/portal/review/:token` convention: the SPA is served from `/`, the hash
// selects the surface, and the DATA lives at the real path `/api/pro/*`. The
// server never sees the hash, so there is nothing to reserve in
// staticWeb.ts's RESERVED_PATH_PREFIXES — a coach screen cannot collide with
// a server route by construction.

type Phase =
  | { kind: 'loading' }
  | { kind: 'needs-profile' }
  | { kind: 'ready'; coach: CoachProfileApi }
  | { kind: 'error'; message: string };

/** The performer label out of `#/pro/students/:label`, or null for `#/pro`.
 *
 * Read from the hash rather than passed in, because App.tsx matches the whole
 * `/pro` prefix with one branch — see its own note on why sub-paths land here
 * rather than falling through to the tenant app. */
function studentLabelFromHash(): string | null {
  const path = window.location.hash.replace(/^#/, '') || '/';
  const m = path.match(/^\/pro\/students\/(.+)$/);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** True on `#/pro/report`. */
function isReportRoute(): boolean {
  return (window.location.hash.replace(/^#/, '') || '/') === '/pro/report';
}

export interface CoachShellProps {
  /** The DEPLOYMENT's product name, read from the pack's entry config rather
   * than asserted by this build. `null` until it answers — a masthead that is
   * briefly blank beats one that names the wrong product to an outside
   * professional. */
  brand?: string | null;
}

export function CoachShell({ brand }: CoachShellProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const student = studentLabelFromHash();
  const onReport = isReportRoute();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCoachMe()
      .then((coach) => {
        if (cancelled) return;
        // null is the ORDINARY answer for anyone who has not signed up as a
        // coach — a state, never an error.
        setPhase(coach ? { kind: 'ready', coach } : { kind: 'needs-profile' });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Loud, not silent: a coach who cannot load their own queue must be
        // told, not shown an empty page that reads as 'no work assigned'.
        setPhase({
          kind: 'error',
          message: err instanceof Error ? err.message : 'We could not reach the server.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmitName(event: Event) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || saving) return;
    setSaving(true);
    try {
      const coach = await saveCoachProfile(trimmed);
      setPhase({ kind: 'ready', coach });
    } catch (err: unknown) {
      setPhase({
        kind: 'error',
        message: err instanceof Error ? err.message : 'We could not save that.',
      });
    } finally {
      setSaving(false);
    }
  }

  if (phase.kind === 'loading') {
    return (
      <div class="coach-surface">
        <PortalFrame brand={brand} footNote="For coaches. Free, always.">
          <p class="coach__quiet">One moment…</p>
        </PortalFrame>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <div class="coach-surface">
        <PortalFrame brand={brand} footNote="For coaches. Free, always.">
          <h1 class="coach__title">We couldn’t load your work</h1>
          <p class="coach__quiet">{phase.message}</p>
        </PortalFrame>
      </div>
    );
  }

  if (phase.kind === 'needs-profile') {
    return (
      <div class="coach-surface">
        <PortalFrame brand={brand} footNote="For coaches. Free, always.">
          <h1 class="coach__title">What name should families see?</h1>
          <p class="coach__quiet">
            This is how you’ll appear on the notes you write. You can change it
            later.
          </p>
          <form class="coach__form" onSubmit={onSubmitName}>
            <input
              class="coach__input"
              type="text"
              value={name}
              maxLength={120}
              autoComplete="name"
              aria-label="Your name"
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
            <button
              class="coach__button"
              type="submit"
              disabled={saving || name.trim().length === 0}
            >
              {saving ? 'Saving…' : 'Continue'}
            </button>
          </form>
        </PortalFrame>
      </div>
    );
  }

  // phase.kind === 'ready'
  return (
    <div class="coach-surface">
      <PortalFrame brand={brand} footNote="For coaches. Free, always.">
        {/* The coach's own name heads the QUEUE. On a student's page the
          student's name heads it instead — two names stacked would read as a
          breadcrumb the surface does not have. */}
      {student === null && !onReport ? (
        <h1 class="coach__title">{phase.coach.displayName}</h1>
      ) : null}
        {/* Rung 3: one student's history, when the hash names one. It reads
          coach_review_log alone, so it outlives every grant — see
          CoachStudent's own note. Otherwise: rung 2's queue. */}
      {student !== null ? (
        <CoachStudent label={student} />
      ) : onReport ? (
        <CoachReport />
      ) : (
        <>
          <CoachQueue />
          {/* The DOORS. Without these, `#/pro/students/:label` and
              `#/pro/report` are reachable only by typing a URL — the exact
              defect ScreenReachability.test.ts exists for. */}
          <CoachStudents />
          {/* A door, not a heading — same shape as RexxHome's own
              `rexx-home__door`. Plain text with a hairline above it read as
              an empty section on the phone walk; the chevron says what the
              line actually is, a place this leads. */}
          <a class="coach-report__link" href="#/pro/report">
            <span>Reviews completed</span>
            <span class="coach-report__link-go" aria-hidden="true">
              ›
            </span>
          </a>
        </>
      )}
      </PortalFrame>
    </div>
  );
}
